import colors from 'shared/colors';
import { chartGroupByOption, gridPageChartType } from 'shared/constants.json';
import { computeLabelStep, dataLabelStyle, escapeHtml, formatNoData, formatTooltipCard, formatTooltipRow, isDateGrouping, nudgeFirstLabel } from '@/composables/grid-page/chart/utils';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

export default function useBarChart(result, chart) {
  const { t } = useI18n();
  const isStacked = computed(() => [gridPageChartType.line, gridPageChartType.bar, gridPageChartType.column].includes(chart.value.chartType)
      && chart.value.aggregationConfig.stackOptions.groupBy !== chartGroupByOption.none);

  const series = computed(() => {
    if (![gridPageChartType.bar, gridPageChartType.column].includes(chart.value.chartType)) {
      return [];
    }

    if (result.value === null) {
      return [];
    }

    return result.value.series;
  });

  const categories = computed(() => {
    if (result.value === null) {
      return [];
    }

    return result.value.categories;
  });

  const isBarType = computed(() => chart.value.chartType === gridPageChartType.bar);

  const categoryAxisConfig = computed(() => {
    if (isDateGrouping(chart.value.groupBy?.groupBy)) {
      return {
        tickInterval: computeLabelStep(categories.value.length),
        labels: { autoRotation: false },
      };
    }
    if (isBarType.value) {
      return { labels: { style: { textOverflow: 'ellipsis', width: '200px' } } };
    }
    return { labels: { autoRotation: [-45], style: { textOverflow: 'ellipsis', width: '80px' } } };
  });

  const chartOptions = computed(() => ({
    chart: {
      animation: false,
      type: chart.value.chartType,
      events: {
        render() {
          nudgeFirstLabel(this);
        },
      },
    },
    plotOptions: {
      [chart.value.chartType]: {
        colorByPoint: !isStacked.value,
        maxPointWidth: 16,
        borderRadius: 3,
        pointPadding: 0,
        groupPadding: 0.2,
        states: {
          hover: { brightness: 0 },
          inactive: { enabled: false },
        },
        dataLabels: {
          enabled: !isStacked.value,
          inside: false,
          overflow: 'allow',
          crop: false,
          style: dataLabelStyle,
          formatter() {
            return this.y === 0 ? null : this.y;
          },
          ...(isBarType.value
            ? { align: 'left', x: 2, verticalAlign: 'middle', y: -1 }
            : { verticalAlign: 'bottom', y: -2 }),
        },
        stacking: 'normal',
      },
    },
    legend: {
      enabled: isStacked.value,
      layout: 'horizontal',
      align: 'center',
      verticalAlign: 'bottom',
      y: 10,
      events: {
        itemClick(e) {
          const chart = this.chart;
          const visibleCount = chart.series.filter((s) => s.visible).length;

          if (visibleCount === 1 && e.legendItem.visible) {
            return false;
          }
          return true;
        },
      },
    },
    xAxis: {
      categories: categories.value,
      showEmpty: series.value.length > 0,
      lineWidth: 1,
      ...categoryAxisConfig.value.tickInterval && { tickInterval: categoryAxisConfig.value.tickInterval },
      labels: {
        enabled: true,
        ...categoryAxisConfig.value.labels,
      },
      crosshair: { color: colors.grey.lighten6, width: 1, zIndex: 0 },
    },
    yAxis: {
      title: { text: null },
      labels: { enabled: true },
      gridLineWidth: 1,
      stackLabels: {
        enabled: isStacked.value,
        style: dataLabelStyle,
        ...(isBarType.value
          ? { x: 2 }
          : { y: -2 }),
      },
    },
    series: series.value,
    tooltip: {
      shared: true,
      formatter() {
        const points = this.points;
        const noData = t('highcharts.noDataShort');
        if (isStacked.value && points.length > 1) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(points[0].key)}</div>`;
          const rows = points.filter((p) => p.y !== 0).map((p) => formatTooltipRow(p.color, p.series.name, p.y)).join('');
          return formatTooltipCard(header + (rows || formatNoData(noData)));
        }
        const p = points[0];
        if (p.y === 0) {
          const header = `<div style="margin-bottom:6px;color:${colors.grey.darken4}">${escapeHtml(p.key || p.point.name)}</div>`;
          return formatTooltipCard(header + formatNoData(noData));
        }
        return formatTooltipCard(formatTooltipRow(p.color, p.key || p.point.name, p.y));
      },
      positioner(labelWidth, labelHeight, point) {
        const { plotLeft, plotTop, plotWidth, plotHeight } = this.chart;
        const clamp = (val, min, max) => Math.min(Math.max(val, min), max);

        if (this.chart.inverted) {
          let x = point.plotX + plotLeft + 16;
          if (x + labelWidth > plotLeft + plotWidth) {
            x = point.plotX + plotLeft - labelWidth - 16;
          }
          return {
            x: clamp(x, plotLeft, plotLeft + plotWidth - labelWidth),
            y: clamp(point.plotY + plotTop - labelHeight / 2, plotTop, plotTop + plotHeight - labelHeight),
          };
        }
        return {
          x: clamp(point.plotX + plotLeft - labelWidth / 2, plotLeft, plotLeft + plotWidth - labelWidth),
          y: Math.max(point.plotY + plotTop - labelHeight - 12, 0),
        };
      },
    },
  }));

  return { chartOptions };
}
